import posthog from 'posthog-js'

type EventMap = {
  auth_sign_in_failed: { error: string }
  // ── Shared ────────────────────────────────────────────────────────────────
  auth_signed_in: { method?: string; role: string }
  auth_signed_out: { role: string }

  booking_cancel_failed: { booking_id: string; reason?: string }
  booking_cancel_initiated: { booking_id: string }
  booking_cancelled: { booking_id: string; turf_id: string }
  booking_confirmation_viewed: { booking_id: string; turf_id: string }

  // ── Web — My Bookings ─────────────────────────────────────────────────────
  booking_detail_viewed: { booking_id: string; status: string }
  // ── Portal — Manager ──────────────────────────────────────────────────────
  booking_queue_filtered: { filter: 'date' | 'status'; value: string }
  check_in_failed: { reason: 'already_checked_in' | 'invalid_code' | 'not_found' | 'wrong_turf' }
  check_in_scanner_opened: Record<string, never>
  check_in_succeeded: { booking_id: string; turf_id: string }
  checkout_viewed: { amount: number; slot_id: string; turf_id: string }
  // ── Web — Booking Flow ────────────────────────────────────────────────────
  date_selected: { date: string; turf_id: string }
  landing_cta_clicked: { cta: 'footer' | 'hero' | 'install' | 'nav' }
  manager_invite_revoked: { member_id: string; org_id: string }

  manager_invite_sent: { org_id: string }
  notification_clicked: { notification_id: string; type: string }
  // ── Web — Notifications ───────────────────────────────────────────────────
  notification_received: { type: string }
  org_detail_viewed: { org_id: string }
  payment_cancelled: { amount: number; booking_id: string }
  payment_failed: { amount: number; booking_id: string; error_code?: string }
  payment_initiated: { amount: number; booking_id: string; turf_id: string }
  payment_succeeded: { amount: number; booking_id: string; sport: string; turf_id: string }
  pwa_install_accepted: Record<string, never>
  pwa_install_dismissed: Record<string, never>
  // ── Web — PWA & Landing ───────────────────────────────────────────────────
  pwa_install_prompted: Record<string, never>
  // ── Web — Reviews ─────────────────────────────────────────────────────────
  review_form_opened: { booking_id: string; turf_id: string }
  review_submission_failed: { error?: string; turf_id: string }

  review_submitted: { booking_id: string; rating: number; turf_id: string }
  settlement_requested: { amount: number; org_id: string }
  slot_cancelled: { slot_id: string; turf_id: string }
  slot_deselected: { slot_id: string; turf_id: string }

  slot_hold_expired: { slot_id: string; turf_id: string }
  slot_hold_failed: {
    reason: 'already_held' | 'error' | 'unavailable'
    slot_id: string
    turf_id: string
  }
  slot_hold_initiated: { price: number; slot_id: string; turf_id: string }

  slot_hold_succeeded: { hold_expires_at: string; slot_id: string; turf_id: string }
  slot_marked_unavailable: { slot_id: string; turf_id: string }

  slot_published: { slot_count: number; turf_id: string }
  slot_selected: {
    price: number
    slot_id: string
    sport: string
    start_time: string
    turf_id: string
  }
  slot_walk_in_booked: { slot_id: string; turf_id: string }
  slots_bulk_created: { date_range_days: number; slot_count: number; turf_id: string }
  slots_created: { slot_count: number; sport: string; turf_id: string }
  // ── Portal — Admin ────────────────────────────────────────────────────────
  turf_approved: { org_id: string; turf_id: string }
  turf_card_clicked: { position: number; turf_id: string; turf_name: string }
  turf_create_failed: { error: string }
  // ── Portal — Owner ────────────────────────────────────────────────────────
  turf_create_started: Record<string, never>
  turf_created: { city: string; sport: string; turf_id: string }
  turf_deactivated: { turf_id: string }
  turf_directions_clicked: { turf_id: string }
  turf_filter_applied: { filter: 'distance' | 'price' | 'sport'; value: string }
  turf_photos_viewed: { turf_id: string }

  turf_rejected: { org_id: string; reason?: string; turf_id: string }
  turf_reviews_viewed: { turf_id: string }
  // ── Web — Discovery & Search ──────────────────────────────────────────────
  turf_search_performed: { city?: string; date?: string; query?: string; sport?: string }
  turf_share_clicked: { turf_id: string }
  turf_sort_changed: { sort: 'distance' | 'price' | 'rating' }

  turf_submitted_for_approval: { turf_id: string }
  turf_updated: { fields_changed: string[]; turf_id: string }
  turf_viewed: { city: string; sport: string; turf_id: string; turf_name: string }
}

type EventProperties<E extends keyof EventMap> =
  EventMap[E] extends Record<string, never> ? [] : [EventMap[E]]

export function track<E extends keyof EventMap>(event: E, ...props: EventProperties<E>): void {
  posthog.capture(event, props[0] as Record<string, unknown> | undefined)
}
